import React, { useState } from "react";
import { FileDown } from "lucide-react";
import "./HeroPriceActionBootcamp.css";
import heroImage from "../../images/Landscape/MCI2.webp";
import chartBg from "../../images/Chart 4.webp";
import EmailModal from "../brochure_modal/brochure_modal";

const APPLY_URL = "https://andus.co.za/trade-register";
const COHORT_START_DATE = new Date(2026, 6, 1, 12);

const bootcampHighlights = [
  {
    id: "market-structure",
    text: "Read market structure, trends, ranges and key turning points.",
    mobileText: "Market structure, trends and turning points.",
  },
  {
    id: "price-action",
    text: "Understand candles, context, pressure and professional price action logic.",
    mobileText: "Candles, context and price action logic.",
  },
  {
    id: "risk-execution",
    text: "Build beginner-friendly execution habits with structured risk management.",
    mobileText: "Execution habits and structured risk.",
  },
];

const bootcampFacts = [
  {
    label: "Modules",
    value: "3",
  },
  {
    label: "Format",
    value: "Part-time online",
  },
  {
    label: "Duration",
    value: "2 months per module",
  },
  {
    label: "Price",
    value: "R6,750 per module",
  },
];

const marketCandles = [
  "green",
  "green",
  "red",
  "green",
  "red",
  "green",
  "green",
  "red",
  "green",
  "green",
  "red",
  "green",
];

const factTrends = ["gold", "green", "green", "red"];
const highlightTrends = ["gold", "green", "red"];

const cohortDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatCohortDate(date) {
  return cohortDateFormatter.format(date);
}

function getDateTimeValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function HeroPriceActionBootcamp() {
  const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);

  const cohortStartDate = COHORT_START_DATE;
  const cohortStartDateLabel = formatCohortDate(cohortStartDate);
  const cohortStartDateTime = getDateTimeValue(cohortStartDate);

  return (
    <>
      <section
        className="trpc-pab-hero trpc-pab-hero-bootcamp"
        aria-labelledby="trpc-pab-hero-title"
      >
        <div className="trpc-pab-hero-frame">
          <div className="trpc-pab-hero-image-container" aria-hidden="true">
            <img
              className="trpc-pab-hero-image"
              src={heroImage}
              alt=""
              decoding="async"
              fetchPriority="high"
            />
            <div className="trpc-pab-hero-image-overlay" />
          </div>

          <div className="trpc-pab-hero-chartLayer" aria-hidden="true">
            <img
              className="trpc-pab-hero-chart"
              src={chartBg}
              alt=""
              decoding="async"
              loading="lazy"
            />
          </div>

          <div className="trpc-pab-hero-marketLayer" aria-hidden="true">
            <span className="trpc-pab-hero-priceLine" />

            <div className="trpc-pab-hero-candleGroup">
              {marketCandles.map((trend, index) => (
                <span
                  key={`${trend}-${index}`}
                  className="trpc-pab-hero-candle"
                  data-trend={trend}
                />
              ))}
            </div>
          </div>

          <div
            className="trpc-pab-hero-cohortBanner"
            role="note"
            aria-label={`Join the next cohort. Start date: ${cohortStartDateLabel}.`}
          >
            <span className="trpc-pab-hero-cohortTitle">
              Join the next cohort
            </span>

            <span className="trpc-pab-hero-cohortDate">
              Start date:{" "}
              <time dateTime={cohortStartDateTime}>
                {cohortStartDateLabel}
              </time>
            </span>
          </div>

          <div className="trpc-pab-hero-content trpc-pab-hero-content-centered">
            <div className="trpc-pab-hero-grid">
              <div className="trpc-pab-hero-text">
                <div
                  className="trpc-pab-hero-kicker"
                  aria-label="TPC Price Action Trader Bootcamp"
                >
                  Professional Trading Bootcamp
                </div>

                <h1 id="trpc-pab-hero-title" className="trpc-pab-hero-title">
                  TPC - Price Action Trader Bootcamp
                </h1>

                <p className="trpc-pab-hero-subtitle">
                  <span className="trpc-pab-hero-subtitleDesktop">
                    A structured, beginner-friendly pathway into technical
                    trading, price action, risk management and disciplined trade
                    execution.
                  </span>
                  <span className="trpc-pab-hero-subtitleMobile">
                    Learn market structure, price action, risk and execution
                    through a structured online pathway.
                  </span>
                </p>

                <p className="trpc-pab-hero-smallprint">
                  Developed in partnership with the London Academy of Trading
                  and world renowned price action specialist Al Brooks.
                </p>

                <div
                  className="trpc-pab-hero-buttons-row"
                  aria-label="Bootcamp actions"
                >
                  <a
                    className="trpc-pab-hero-btn trpc-pab-hero-btn-gold"
                    href={APPLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-candle="green"
                  >
                    <span>Apply Now</span>
                  </a>

                  {!isBrochureModalOpen && (
                    <button
                      className="trpc-pab-hero-btn trpc-pab-hero-btn-outline"
                      type="button"
                      onClick={() => setIsBrochureModalOpen(true)}
                      data-candle="red"
                      aria-label="Download the TPC Price Action Trader Bootcamp brochure"
                    >
                      <span>Download Brochure</span>
                      <FileDown
                        size={17}
                        strokeWidth={2.4}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </div>
              </div>

              <aside
                className="trpc-pab-hero-terminalPanel"
                aria-label="Bootcamp information"
              >
                <div className="trpc-pab-hero-mobilePanelHeading">
                  Bootcamp details
                </div>

                <div className="trpc-pab-hero-panelTop" aria-hidden="true">
                  <div className="trpc-pab-hero-panelTicker">
                    {marketCandles.slice(0, 7).map((trend, index) => (
                      <span
                        key={`panel-${trend}-${index}`}
                        className="trpc-pab-hero-miniCandle"
                        data-trend={trend}
                      />
                    ))}
                  </div>
                </div>

                <div
                  className="trpc-pab-hero-facts"
                  aria-label="Bootcamp information"
                >
                  {bootcampFacts.map((fact, index) => (
                    <div
                      className="trpc-pab-hero-fact"
                      key={fact.label}
                      data-trend={factTrends[index] || "green"}
                    >
                      <span className="trpc-pab-hero-factLabel">
                        {fact.label}
                      </span>
                      <span className="trpc-pab-hero-factValue">
                        {fact.value}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="trpc-pab-hero-learnCard">
                  <div className="trpc-pab-hero-learnHeading">
                    What you will learn
                  </div>

                  <ul className="trpc-pab-hero-bullets">
                    {bootcampHighlights.map((item, index) => (
                      <li
                        key={item.id}
                        data-trend={highlightTrends[index] || "green"}
                      >
                        <span className="trpc-pab-hero-bulletDesktop">
                          {item.text}
                        </span>
                        <span className="trpc-pab-hero-bulletMobile">
                          {item.mobileText}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>

      <EmailModal
        isOpen={isBrochureModalOpen}
        onClose={() => setIsBrochureModalOpen(false)}
      />
    </>
  );
}

export default HeroPriceActionBootcamp;